import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import {
  summarise, parseJson, mealEntries, movementParts, sleepParts, supplementsList,
} from '../../lib/careLogSummary'

const SHIFT_LABEL = { morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening', night: 'Night', full_day: 'Full day' }
const MOOD_FACES = ['😟', '😕', '😐', '🙂', '😊']
const BEHAVIOURS = [
  ['behaviour_agitation', 'Agitation'], ['behaviour_wandering', 'Wandering'], ['behaviour_confusion', 'Confusion'],
  ['behaviour_sundowning', 'Sundowning'], ['behaviour_sleep_disturbance', 'Sleep disturbance'],
  ['behaviour_appetite_change', 'Appetite change'], ['behaviour_fall', 'Fall'],
]
const TASKS = [['task_hygiene', 'Hygiene'], ['task_medications', 'Medications'], ['task_mobility', 'Mobility'], ['task_activities', 'Activities']]

const fmtDate = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })

export default function CareLogCard({ log }) {
  const [open, setOpen] = useState(false)
  const [photoUrl, setPhotoUrl] = useState(null)

  const moods = [log.mood_morning, log.mood_afternoon, log.mood_evening].filter((m) => m != null)
  const moodAvg = moods.length ? moods.reduce((a, b) => a + b, 0) / moods.length : null
  const flaggedBehaviours = BEHAVIOURS.filter(([k]) => log[k])
  const doneTasks = TASKS.filter(([k]) => log[k])
  const loggerName = log.profile?.full_name || 'Someone'
  const s = summarise(log)

  async function toggle() {
    const next = !open
    setOpen(next)
    if (next && log.photo_url && !photoUrl) {
      const { data } = await supabase.storage.from('care-photos').createSignedUrl(log.photo_url, 3600)
      if (data?.signedUrl) setPhotoUrl(data.signedUrl)
    }
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <button className="card-tap" onClick={toggle} style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: 16 }}>
        <div className="spread">
          <div>
            <div className="row" style={{ gap: 8 }}>
              <span style={{ fontWeight: 700 }}>{fmtDate(log.log_date)}</span>
              <span className="badge">{SHIFT_LABEL[log.shift] || log.shift}</span>
            </div>
            <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>by {loggerName}</div>
          </div>
          <div className="row" style={{ gap: 6 }}>
            {moodAvg != null && <span style={{ fontSize: 22 }}>{MOOD_FACES[Math.round(moodAvg) - 1]}</span>}
            {log.behaviour_fall && <span className="badge badge-danger">Fall</span>}
          </div>
        </div>
        {/* Summary line */}
        <div className="row wrap" style={{ gap: 6, marginTop: 8 }}>
          {doneTasks.length > 0 && <span className="badge badge-green">{doneTasks.length} task{doneTasks.length > 1 ? 's' : ''} ✓</span>}
          {flaggedBehaviours.filter(([k]) => k !== 'behaviour_fall').map(([k, l]) => <span key={k} className="badge badge-amber">{l}</span>)}
          {log.photo_url && <span className="badge">📷 Photo</span>}
        </div>
        {log.notes && !open && (
          <p className="muted" style={{ margin: '8px 0 0', fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.notes}</p>
        )}
      </button>

      {open && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border)' }}>
          <div className="stack mt-16" style={{ gap: 12 }}>
            {/* What was logged, at a glance */}
            {(s.meals || s.water || s.movement || s.sleep || s.supplements > 0) && (
              <div className="row wrap" style={{ gap: 6 }}>
                {s.meals && <span className="badge">🍽 {s.meals}</span>}
                {s.water && <span className="badge">💧 {s.water}</span>}
                {s.movement && <span className="badge">🚶 {s.movement}</span>}
                {s.sleep && <span className="badge">😴 {s.sleep}</span>}
                {s.supplements > 0 && <span className="badge">💊 {s.supplements} supplement{s.supplements > 1 ? 's' : ''}</span>}
              </div>
            )}
            {(log.shift_start || log.shift_end) && (
              <Detail label="Shift time" value={`${log.shift_start?.slice(0, 5) || '—'} to ${log.shift_end?.slice(0, 5) || '—'}`} />
            )}
            {moods.length > 0 && (
              <div>
                <div className="section-title">Mood</div>
                <div className="row" style={{ gap: 12, marginTop: 4 }}>
                  {log.mood_morning != null && <span>AM {MOOD_FACES[log.mood_morning - 1]}</span>}
                  {log.mood_afternoon != null && <span>PM {MOOD_FACES[log.mood_afternoon - 1]}</span>}
                  {log.mood_evening != null && <span>Eve {MOOD_FACES[log.mood_evening - 1]}</span>}
                </div>
              </div>
            )}
            {/* Prefer the structured meals; meals_notes is the pre-migration fallback. */}
            {parseJson(log.meals_json)
              ? <Detail label="Meals" value={mealsDetail(log.meals_json)} />
              : log.meals_notes && <Detail label="Meals" value={log.meals_notes} />}
            {log.fluids_ml != null && (
              <Detail label="Fluids" value={s.water ? `${s.water} · ${log.fluids_ml} ml` : `${log.fluids_ml} ml`} />
            )}
            {s.movement && <Detail label="Movement" value={movementDetail(log.movement_json)} />}
            {s.sleep && <Detail label="Sleep" value={sleepDetail(log.sleep_json)} />}
            {s.supplements > 0 && <Detail label="Supplements" value={supplementsDetail(log.supplements_json)} />}
            {doneTasks.length > 0 && <Detail label="Tasks" value={doneTasks.map(([, l]) => l).join(', ')} />}
            {(flaggedBehaviours.length > 0 || log.behaviour_other) && (
              <Detail label="Behaviours" value={[...flaggedBehaviours.map(([, l]) => l), log.behaviour_other].filter(Boolean).join(', ')} />
            )}
            {log.notes && <Detail label="Notes" value={log.notes} />}
            {log.photo_url && (
              photoUrl ? (
                <img src={photoUrl} alt="Care log attachment" style={{ width: '100%', borderRadius: 10, maxHeight: 320, objectFit: 'cover' }} />
              ) : (
                <div className="muted" style={{ fontSize: 13 }}>Loading photo…</div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function mealsDetail(meals_json) {
  return mealEntries(meals_json)
    .map((e) => `${e.label}: ${[e.statusLabel, e.detail].filter(Boolean).join(' — ')}`)
    .join('\n')
}

function movementDetail(movement_json) {
  const p = movementParts(movement_json)
  if (!p) return ''
  const bits = []
  if (p.times.length) bits.push(p.times.join(', '))
  if (p.activity) bits.push(p.activity)
  if (p.duration) bits.push(`${p.duration} min`)
  if (p.steps) bits.push(`${p.steps} steps`)
  if (p.notes) bits.push(p.notes)
  return bits.join(' · ')
}

function sleepDetail(sleep_json) {
  const p = sleepParts(sleep_json)
  if (!p) return ''
  const bits = []
  if (p.bedtime || p.wake) {
    bits.push(`${String(p.bedtime || '').slice(0, 5) || '—'} to ${String(p.wake || '').slice(0, 5) || '—'}`)
  }
  if (p.hours != null) bits.push(`${p.hours}h`)
  if (p.qualityLabel) bits.push(p.qualityLabel)
  if (p.rested) bits.push('woke rested')
  return bits.join(' · ')
}

function supplementsDetail(supplements_json) {
  return supplementsList(supplements_json)
    .map((x) => [x.name, x.dose, x.time].filter(Boolean).join(' · '))
    .join('\n')
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="section-title">{label}</div>
      <div style={{ fontSize: 15, marginTop: 2, whiteSpace: 'pre-wrap' }}>{value}</div>
    </div>
  )
}
